import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { api, apiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";

export default function Ledgers() {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tb, setTb] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState(null);
  const [form, setForm] = useState({ name: "", group: "", opening_balance: 0, opening_dc: "Dr" });

  const load = async () => {
    const [l, g, t] = await Promise.all([
      api.get("/ledgers"), api.get("/ledger-groups"), api.get("/reports/trial-balance"),
    ]);
    setRows(l.data);
    setGroups(g.data);
    setTb(t.data);
  };
  useEffect(() => { load(); }, []);

  const balOf = (id) => tb?.rows?.find((r) => r.ledger_id === id);

  const create = async () => {
    try {
      await api.post("/ledgers", { ...form, opening_balance: Number(form.opening_balance) });
      toast.success("Ledger created");
      setOpen(false);
      setForm({ name: "", group: "", opening_balance: 0, opening_dc: "Dr" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openStatement = async (id) => {
    const { data } = await api.get(`/reports/ledger/${id}`);
    setStatement(data);
  };

  const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.group || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="ledgers-page">
      <PageHeader title="Chart of Accounts" subtitle={`${rows.length} ledgers across ${groups.length} groups · Schedule III grouping`}>
        <Input placeholder="Filter ledgers…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-8 w-56 text-sm" data-testid="ledger-search-input" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="new-ledger-btn">New ledger</Button>
          </DialogTrigger>
          <DialogContent data-testid="ledger-dialog">
            <DialogHeader><DialogTitle className="font-display">Create ledger</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Name</Label>
                <Input data-testid="ledger-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Group</Label>
                <Select value={form.group} onValueChange={(v) => setForm({ ...form, group: v })}>
                  <SelectTrigger data-testid="ledger-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {groups.map((g) => <SelectItem key={g.id} value={g.name}>{g.name} · {g.nature}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest">Opening balance</Label>
                  <Input type="number" className="num" data-testid="ledger-opening-input" value={form.opening_balance}
                    onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest">Dr / Cr</Label>
                  <Select value={form.opening_dc} onValueChange={(v) => setForm({ ...form, opening_dc: v })}>
                    <SelectTrigger data-testid="ledger-dc-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Dr">Dr</SelectItem><SelectItem value="Cr">Cr</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={!form.name || !form.group} data-testid="save-ledger-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {tb ? (
        <div className="flex flex-wrap gap-px border border-border bg-border text-sm">
          {[["Total debit", tb.total_debit], ["Total credit", tb.total_credit]].map(([l, v]) => (
            <div key={l} className="flex-1 bg-background p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</p>
              <p className="num mt-1 font-semibold">{money(v)}</p>
            </div>
          ))}
          <div className="flex-1 bg-background p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Trial balance</p>
            <p className={`mt-1 font-semibold ${tb.balanced ? "text-success" : "text-destructive"}`} data-testid="tb-balanced">
              {tb.balanced ? "Balanced" : "Out of balance"}
            </p>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto border border-border">
        <table className="dense-table w-full min-w-[840px]" data-testid="ledgers-table">
          <thead>
            <tr>
              <th>Ledger</th><th>Group</th><th>Nature</th>
              <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Closing</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const b = balOf(l.id);
              return (
                <tr key={l.id} className="cursor-pointer row-in" onClick={() => openStatement(l.id)} data-testid={`ledger-row-${l.id}`}>
                  <td className="font-medium">
                    {l.name}
                    {l.is_system ? <span className="ml-2 bg-muted px-1 text-[10px] uppercase tracking-widest text-muted-foreground">system</span> : null}
                  </td>
                  <td className="text-muted-foreground">{l.group}</td>
                  <td className="text-muted-foreground">{l.nature}</td>
                  <td className="num text-right">{b ? money(b.debit) : "—"}</td>
                  <td className="num text-right">{b ? money(b.credit) : "—"}</td>
                  <td className="num text-right font-medium">{b ? `${money(b.closing)} ${b.closing_dc}` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Sheet open={!!statement} onOpenChange={(o) => !o && setStatement(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="ledger-statement-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">{statement?.ledger?.name}</SheetTitle>
          </SheetHeader>
          {statement ? (
            <div className="mt-4 space-y-4 text-sm">
              <div className="flex gap-px border border-border bg-border">
                <div className="flex-1 bg-background p-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Opening</p>
                  <p className="num">{money(statement.opening)} {statement.opening_dc}</p>
                </div>
                <div className="flex-1 bg-background p-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Closing</p>
                  <p className="num font-semibold">{money(statement.closing)} {statement.closing_dc}</p>
                </div>
              </div>
              <table className="dense-table w-full">
                <thead><tr><th>Date</th><th>Particulars</th><th className="text-right">Dr</th><th className="text-right">Cr</th><th className="text-right">Balance</th></tr></thead>
                <tbody>
                  {statement.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="num whitespace-nowrap">{fmtDate(l.date)}</td>
                      <td>
                        <p className="truncate">{l.particulars}</p>
                        <p className="num text-[10px] text-muted-foreground">{l.type} · {l.number}</p>
                      </td>
                      <td className="num text-right">{l.debit ? money(l.debit) : "—"}</td>
                      <td className="num text-right">{l.credit ? money(l.credit) : "—"}</td>
                      <td className="num text-right">{money(l.balance)} {l.balance_dc}</td>
                    </tr>
                  ))}
                  {statement.lines.length === 0 && <tr><td colSpan={5} className="text-muted-foreground">No transactions.</td></tr>}
                </tbody>
              </table>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
